import { useEffect, useState } from "react";
import { ArrowUpRight, Menu, Phone, X } from "lucide-react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { ButtonLink, cn } from "./ui";

const nav = [
  ["Diensten", "/diensten"],
  ["Cases", "/cases"],
  ["Over STAQL", "/over-staql"],
  ["Contact", "/contact"]
] as const;

function Brand() {
  return (
    <Link className="brand" to="/" aria-label="STAQL home">
      <img className="brand__mark" src="/media/staql-wordmark.png" alt="STAQL" width="407" height="106" />
    </Link>
  );
}

function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header className="site-header">
      <div className="container site-header__inner">
        <Brand />
        <nav className="desktop-nav" aria-label="Hoofdnavigatie">
          {nav.map(([label, path]) => (
            <NavLink key={path} to={path} className={({ isActive }) => cn("nav-link", isActive && "nav-link--active")}>{label}</NavLink>
          ))}
        </nav>
        <div className="site-header__actions">
          <ButtonLink className="header-cta" to="/contact" showArrow>Plan een kennismaking</ButtonLink>
          <button className="menu-toggle" type="button" aria-expanded={open} aria-controls="mobile-menu" aria-label={open ? "Menu sluiten" : "Menu openen"} onClick={() => setOpen(!open)}>
            {open ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
          </button>
        </div>
      </div>
      <div className={cn("mobile-menu", open && "mobile-menu--open")} id="mobile-menu">
        <nav className="container" aria-label="Mobiele navigatie">
          {nav.map(([label, path]) => <NavLink key={path} to={path}>{label}<ArrowUpRight aria-hidden="true" size={18} /></NavLink>)}
          <ButtonLink to="/contact" showArrow>Plan een kennismaking</ButtonLink>
        </nav>
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer className="site-footer">
      <div className="container">
        <div className="footer-top">
          <div className="footer-brand">
            <Brand />
            <p className="footer-brand__tagline">Technologie die voor je werkt.</p>
            <p>STAQL helpt kleine bedrijven en het MKB met data, automatisering, software en slimme systemen.</p>
          </div>
          <div className="footer-column">
            <h2>Diensten</h2>
            <Link to="/diensten/data-ai">Data & AI</Link>
            <Link to="/diensten/automatisering-software">Automatisering & software</Link>
            <Link to="/diensten/iot-slimme-systemen">IoT & slimme systemen</Link>
          </div>
          <div className="footer-column">
            <h2>STAQL</h2>
            <Link to="/over-staql">Over STAQL</Link>
            <Link to="/cases">Cases</Link>
          </div>
          <div className="footer-column footer-contact">
            <h2>Contact</h2>
            <span>Botterstraat 47<br />1271 XL Huizen</span>
            <a href="tel:+31353690150"><Phone aria-hidden="true" size={15} />035 369 01 50</a>
            <Link to="/contact">Plan een kennismaking</Link>
          </div>
        </div>
        <div className="footer-bottom">
          <p>© {new Date().getFullYear()} STAQL. Alle rechten voorbehouden.</p>
          <div><Link to="/privacy">Privacy</Link></div>
        </div>
        <p className="footer-service-area"><strong>Werkgebied:</strong> Hilversum · Blaricum · Naarden · Huizen · Almere en de rest van het Gooi.</p>
        <p className="footer-legal">STAQL is gevestigd aan Botterstraat 47, 1271 XL Huizen en ingeschreven bij de Kamer van Koophandel onder nummer 85053716. Klantopdrachten worden uitgevoerd vanuit de eenmanszaak STAQL. Onder STAQL Ventures werken we aan eigen initiatieven, intellectueel eigendom en selectieve venture-samenwerkingen.</p>
      </div>
    </footer>
  );
}

function ScrollManager() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [pathname]);
  return null;
}

export function SiteLayout() {
  return <>
    <ScrollManager />
    <a className="skip-link" href="#main-content">Ga naar de inhoud</a>
    <Header />
    <main id="main-content"><Outlet /></main>
    <Footer />
  </>;
}
